import React from 'react'
import { Button, Typography } from 'antd'
import { ArrowRight, Check } from 'lucide-react'
import { marketingSlogans, usePageTitle } from '../../utils/pageTitle'
import { useNavigate } from 'react-router-dom'
import { useApi } from '../../services/ApiContext'
import {
  getPricingPlans,
  getGeoFromTimezone,
  pricingByGeo,
} from '../../data/pricing'

const { Title, Text } = Typography

export const PricingPage: React.FC = () => {
  usePageTitle(marketingSlogans[0])
  const navigate = useNavigate()
  const api = useApi()

  const geo = getGeoFromTimezone()
  const pricing = pricingByGeo[geo]
  const monthlyPlan = getPricingPlans('monthly')[0]
  const yearlyPlan = getPricingPlans('yearly')[0]
  const features = monthlyPlan.features
    .filter((f) => f.included)
    .map((f) => f.name)

  const handleSelect = async () => {
    if (
      localStorage.getItem('auth_token') &&
      localStorage.getItem('signed_up')
    ) {
      const { url } = await api.createCheckoutSession('monthly')
      window.location.href = url
    } else {
      navigate('/signup')
    }
  }

  return (
    <div id="pricing" className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-12">
          <Title level={1} className="text-4xl font-bold mb-4">
            Simple, transparent pricing
          </Title>
          <Text className="text-xl text-gray-600">
            One plan, everything included. No per-seat charges.
          </Text>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Yearly */}
          <div className="relative flex flex-col rounded-2xl border border-primary bg-white p-8 shadow-md transition-all hover:shadow-lg">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="rounded-full bg-primary px-4 py-1 text-xs font-semibold text-white">
                Recommended
              </span>
            </div>

            <Title level={3} className="mb-1">
              Yearly
            </Title>
            <Text className="mb-6 block text-gray-500">
              Save 20% with annual billing
            </Text>

            <div className="mb-6">
              <span className="text-5xl font-bold tracking-tight">
                {pricing.currencySymbol}
                {yearlyPlan.price}
              </span>
              <Text type="secondary" className="ml-2 text-lg">
                per month, billed yearly
              </Text>
            </div>

            <Button
              type="primary"
              size="large"
              className="mb-8 h-12 w-full"
              onClick={handleSelect}
            >
              <span className="flex items-center justify-center gap-2">
                Continue
                <ArrowRight className="h-5 w-5" />
              </span>
            </Button>

            <div className="space-y-3">
              {features.map((feature) => (
                <div key={feature} className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                  <Text className="text-sm">{feature}</Text>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly */}
          <div className="relative flex flex-col rounded-2xl border border-gray-200 bg-white p-8 shadow-sm transition-all hover:shadow-lg">
            <Title level={3} className="mb-1">
              Monthly
            </Title>
            <Text className="mb-6 block text-gray-500">
              Flexible month-to-month billing
            </Text>

            <div className="mb-6">
              <span className="text-5xl font-bold tracking-tight">
                {pricing.currencySymbol}
                {monthlyPlan.price}
              </span>
              <Text type="secondary" className="ml-2 text-lg">
                per month
              </Text>
            </div>

            <Button
              type="default"
              size="large"
              className="mb-8 h-12 w-full"
              onClick={handleSelect}
            >
              <span className="flex items-center justify-center gap-2">
                Continue
                <ArrowRight className="h-5 w-5" />
              </span>
            </Button>

            <div className="space-y-3">
              {features.map((feature) => (
                <div key={feature} className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0 text-green-500" />
                  <Text className="text-sm">{feature}</Text>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
